import { ToastDescriptor } from '@/state/ToastContext';

/**
 * Helper functions for creating common toast patterns
 */

interface ToastOptions {
  message: string;
  detail?: string;
  autoClose?: number | false;
  action?: {
    label: string;
    onClick: () => void;
  };
}

/**
 * Create a success toast
 */
export const successToast = (options: ToastOptions): Omit<ToastDescriptor, 'id'> => ({
  tone: 'success',
  autoClose: 4000,
  ...options,
});

/**
 * Create an error toast
 */
export const errorToast = (options: ToastOptions): Omit<ToastDescriptor, 'id'> => ({
  tone: 'error',
  autoClose: 6000, // Longer duration for errors
  ...options,
});

/**
 * Create a warning toast
 */
export const warningToast = (options: ToastOptions): Omit<ToastDescriptor, 'id'> => ({
  tone: 'warning',
  autoClose: 5000,
  ...options,
});

/**
 * Create an info toast
 */
export const infoToast = (options: ToastOptions): Omit<ToastDescriptor, 'id'> => ({
  tone: 'info',
  autoClose: 4000,
  ...options,
});

/**
 * Create a persistent toast (no auto-close)
 */
export const persistentToast = (
  options: Omit<ToastOptions, 'autoClose'> & { tone?: 'info' | 'success' | 'warning' | 'error' }
): Omit<ToastDescriptor, 'id'> => ({
  tone: 'info',
  autoClose: false,
  ...options,
});

/**
 * Common toast patterns
 */
export const toastPatterns = {
  // Data operations
  dataSaved: (itemType: string = '資料') =>
    successToast({
      message: '儲存成功',
      detail: `${itemType}已成功儲存`,
    }),

  dataDeleted: (itemType: string = '資料') =>
    successToast({
      message: '刪除成功',
      detail: `${itemType}已成功刪除`,
    }),

  dataUpdated: (itemType: string = '資料') =>
    successToast({
      message: '更新成功',
      detail: `${itemType}已成功更新`,
    }),

  // Error patterns
  saveFailed: (reason?: string) =>
    errorToast({
      message: '儲存失敗',
      detail: reason || '請稍後再試或聯絡系統管理員',
    }),

  loadFailed: (reason?: string) =>
    errorToast({
      message: '載入失敗',
      detail: reason || '無法載入資料，請重新整理頁面',
    }),

  networkError: () =>
    errorToast({
      message: '網路錯誤',
      detail: '無法連線到伺服器，請檢查網路連線',
      autoClose: false,
    }),

  // Validation patterns
  validationError: (message: string) =>
    warningToast({
      message: '驗證失敗',
      detail: message,
    }),

  // Action patterns
  actionRequired: (message: string, action: { label: string; onClick: () => void }) =>
    warningToast({
      message,
      autoClose: false,
      action,
    }),

  // Info patterns
  infoMessage: (message: string, detail?: string) =>
    infoToast({
      message,
      detail,
    }),

  // Copy/clipboard
  copiedToClipboard: () =>
    successToast({
      message: '已複製',
      detail: '內容已複製到剪貼簿',
      autoClose: 2000,
    }),
};
